import React, { useEffect, ReactNode, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { Loader2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { widgetApi } from '@/services/api'
import { safeStorage } from '@/utils/storage'

interface OnboardingGuardProps {
	children: ReactNode
}

export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
	const { user, isInitialized: authInitialized } = useAuth()
	const { teamId, isInitialized: teamInitialized } = useTeamContext()
	const { programs, loading: programsLoading, programId } = useProgramContext()
	const navigate = useNavigate()
	const location = useLocation()
	const queryClient = useQueryClient()

	// Track if user has completed onboarding to prevent redirect loops
	// IMPORTANT: Initialize eagerly to prevent flash of incorrect state
	const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean>(() => {
		// Check immediately on mount, not in useEffect
		if (user?.id) {
			return safeStorage.isOnboardingComplete(user.id)
		}
		return false
	})

	// Update when user changes
	useEffect(() => {
		if (user?.id) {
			const isComplete = safeStorage.isOnboardingComplete(user.id)
			setHasCompletedOnboarding(isComplete)
		} else {
			setHasCompletedOnboarding(false)
		}
	}, [user?.id])

	const { data: widgetsData, isLoading: widgetsLoading } = useQuery({
		queryKey: ['widgets', programId],
		queryFn: () => {
			if (!programId) {
				return Promise.resolve({ data: [], totalCount: 0 })
			}
			return widgetApi.getWidgets(programId)
		},
		enabled: !!programId && !!teamId,
		refetchOnWindowFocus: true,
	})

	const hasWidgets = widgetsData?.data && widgetsData.data.length > 0

	const isLoading = !authInitialized || !teamInitialized || programsLoading

	useEffect(() => {
		if (location.pathname === '/onboarding' && programId && teamId && !hasWidgets && !isLoading) {
			const interval = setInterval(() => {
				queryClient.refetchQueries({ queryKey: ['widgets', programId] })
			}, 2000)

			return () => clearInterval(interval)
		}
	}, [location.pathname, programId, teamId, hasWidgets, isLoading, queryClient])

	useEffect(() => {
		if (isLoading) return // Wait for all data to load

		if (!user) {
			// This case should ideally be caught by ProtectedRoute first
			// but as a safeguard:
			if (location.pathname !== '/auth') {
				navigate('/auth', { replace: true, state: { from: location } })
			}
			return
		}

		// Check if we're coming directly from onboarding completion
		const fromOnboarding = location.state?.fromOnboarding

		// Onboarding logic:
		// - Show onboarding ONLY for brand new users with NO programs at all
		// - If user has ANY program (owner or invited member), NO onboarding
		// - Once a user has a program, they never see onboarding again, even if they create new programs
		// 
		// This covers:
		// 1. New user not invited to any program → onboarding ✓
		// 2. User with existing projects/widgets creating new project → NO onboarding ✓
		// 3. Invited member to a program → NO onboarding ✓
		const hasAnyProgram = programs.length > 0
		const needsOnboarding = !hasAnyProgram && !programsLoading



		if (location.pathname === '/onboarding') {
			if (!needsOnboarding) {

				// Already has access to programs, redirect to dashboard
				navigate('/dashboard', { replace: true })
			}
			// Otherwise, stay on onboarding page
		} else {
			// User is on a page other than /onboarding

			// Don't redirect if:
			// 1. Coming directly from onboarding completion
			// 2. User has completed onboarding before (prevents redirect loops)
			// 3. Programs are still loading (prevents race condition)
			// 4. User has at least one program (owner or invited member)
			const shouldSkipRedirect = fromOnboarding || hasCompletedOnboarding || programsLoading || hasAnyProgram

			if (!shouldSkipRedirect && needsOnboarding) {
				// User genuinely needs onboarding and hasn't completed it before
				// Clear the completion flag since they need to go through onboarding
				if (user?.id) {
					safeStorage.clearOnboardingComplete(user.id)
					setHasCompletedOnboarding(false)
				}
				// Redirect to onboarding page
				navigate('/onboarding', { replace: true })
			}
			// If has programs or completed onboarding, allow rendering children
		}
	}, [user, teamId, programs, programId, programsLoading, isLoading, navigate, location, hasCompletedOnboarding])

	if (isLoading) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Loader2 className="h-10 w-10 animate-spin text-primary" />
			</div>
		)
	}

	// Determine if we should render the children (dashboard)
	const fromOnboarding = location.state?.fromOnboarding
	const isOnOnboardingPage = location.pathname === '/onboarding'
	const hasAnyProgram = programs.length > 0

	// Render children (dashboard) if ANY of these conditions are true:
	// 1. User is on the onboarding page itself
	// 2. User has at least one program (owner or invited member)
	// 3. User completed onboarding before (prevents redirect loops)
	// 4. User is coming directly from onboarding (even if data is still loading)
	const shouldRenderChildren =
		isOnOnboardingPage ||
		hasAnyProgram ||
		hasCompletedOnboarding ||
		fromOnboarding

	if (shouldRenderChildren) {
		return <>{children}</>
	}

	// Fallback loader (should only be hit briefly during initial loading)
	return (
		<div className="flex h-screen w-full items-center justify-center">
			<Loader2 className="h-10 w-10 animate-spin text-primary" />
		</div>
	)
}